import { t } from "@lingui/macro";
import { useState } from "react";

import { WithRealSafeAreaInsets } from "@/hooks/useRealSafeAreaInsets";
import { CheckBox } from "@whoosh/design-system/src/components/controls";
import { PopUpModal } from "@whoosh/design-system/src/components/overlay/PopUpModal";
import {
  VStack,
  Text,
  HStack,
} from "@whoosh/design-system/src/components/primitive";

import { useHandleLogOutCleanUp } from "../../../features/Authentication/hooks/useHandleLogOutCleanUp";
import { disableBiometricUnlock } from "../../../features/BiometricAuthentication/biometricUnlock";
import { useMutationWithError } from "../../../utils/hooks/useMutationWithError";
import { DeleteMemberUserMutationString } from "../mutations/DeleteMemberUser";

type DeleteAccountModalProps = {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
};

const DeleteAccountModal = ({ isOpen, setIsOpen }: DeleteAccountModalProps) => {
  const [isDeleteAccountCheckConfirmed, setIsDeleteAccountCheckConfirmed] =
    useState(false);

  const [tryDeleteMemberUser] = useMutationWithError(
    DeleteMemberUserMutationString
  );

  const handleLogOutCleanUp = useHandleLogOutCleanUp();
  const onDeletePress = () => {
    if (!isDeleteAccountCheckConfirmed) return;
    tryDeleteMemberUser({
      variables: {},
      onCompleted: async () => {
        try {
          await disableBiometricUnlock();
        } finally {
          await handleLogOutCleanUp();
        }
      },
    });
    setIsOpen(false);
  };

  return (
    <WithRealSafeAreaInsets>
      <PopUpModal
        upperTitle={t`Delete Account`}
        cancelText={t`Dismiss`}
        confirmText={t`Delete Account`}
        theme="danger"
        isConfirmDisabled={!isDeleteAccountCheckConfirmed}
        onClose={(confirm: boolean) => {
          if (confirm) {
            onDeletePress();
          } else {
            setIsOpen(false);
          }
        }}
        isOpen={isOpen}
        content={
          <VStack>
            <VStack gap={4} mb={16}>
              <Text
                maxFontSizeMultiplier={2}
                variant="mainBaseSemiBold"
                color="text-secondary"
              >
                {t`Are you sure you want to permanently delete your account?`}
              </Text>
              <Text
                maxFontSizeMultiplier={2}
                variant="mainBaseSemiBold"
                color="text-secondary"
              >
                {t`You will lose all preferences.`}
              </Text>
            </VStack>
            <HStack mb={8} gap={2} alignItems="center">
              <CheckBox
                isChecked={isDeleteAccountCheckConfirmed}
                onPress={() =>
                  setIsDeleteAccountCheckConfirmed(
                    !isDeleteAccountCheckConfirmed
                  )
                }
              />
              <Text
                maxFontSizeMultiplier={2}
                variant="mainBaseSemiBold"
                color="grayscale-1000"
              >
                {t`I am aware. Please delete my account`}
              </Text>
            </HStack>
          </VStack>
        }
      />
    </WithRealSafeAreaInsets>
  );
};

export default DeleteAccountModal;
